import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  deleteFreightClaimAttachmentsCall,
  getPhysicalCreditTable,
  postInventoryAdjustment,
  uploadFreightClaimAttachmentsCall
} from "src/api/sp";
import { resetStore } from "src/store/actions";
import stringToNumber from "src/utils/stringToNumber";
import { AttachmentsInitialState, UploadAttachmentFile } from "./interface";
import { getAdjustedItems } from "src/utils/inventoryUtils";
import { transformDataToDepartment } from "./components/Adjustment";

export const createInventoryAdjustment = createAsyncThunk(
  "createInventoryAdjustment",
  (payload: any) => postInventoryAdjustment(payload)
);

export const fetchCreditTable = createAsyncThunk(
  "InventoryAdjustment",
  (props: any) => getPhysicalCreditTable(props)
);

export const uploadAttachments = createAsyncThunk(
  "uploadInventoryAdjustmentAttachments",
  (uploadAttachmentPayload: UploadAttachmentFile) =>
    uploadFreightClaimAttachmentsCall(uploadAttachmentPayload)
);

export const deleteAttachments = createAsyncThunk(
  "deleteInventoryAdjustmentAttachments",
  (filePath: string) => {
    deleteFreightClaimAttachmentsCall(filePath);
  }
);

const commonCreditValuation = {
  id: "valuation",
  isSeparated: true,
  noAdornment: true,
  type: "text",
  disabled: true,
  hideInput: true,
  align: "left"
};

const deniedCreditsValuation = {
  valuation: {
    ...commonCreditValuation,
    value: "Denied Credits"
  }
};

const pendingCreditsValuation = {
  valuation: {
    ...commonCreditValuation,
    value: "Pending Credits"
  }
};
const initialAdjustment = {
  grocery: {
    id: "grocery",
    value: "",
    testId: "grocery-input"
  },
  frozen: {
    id: "frozen",
    value: ""
  },
  deli: {
    id: "deli",
    value: ""
  },
  beerWine: {
    id: "beerWine",
    value: ""
  },
  hbc: {
    id: "hbc",
    value: ""
  },
  gm: {
    id: "gm",
    value: ""
  },
  produce: {
    id: "produce",
    value: ""
  },
  meat: {
    id: "meat",
    isSeparated: true,
    value: ""
  },
  total: {
    id: "total",
    value: "",
    disabled: true
  }
};
export const initialState: any = {
  adjustment: { ...pendingCreditsValuation, ...initialAdjustment },
  deniedCredits: { ...deniedCreditsValuation, ...initialAdjustment },
  notes: "",
  blockNavigation: true,
  loading: false,
  createdBy: "",
  attachments: [],
  deletedAttachmentPaths: [],
  pendingCreditsData: {}
};

const createInventoryAdjustmentSlice = createSlice({
  name: "createInventoryAdjustment",
  initialState,
  reducers: {
    setAttachment(
      state: AttachmentsInitialState,
      action: {
        payload: { fileName: string; fileURL: string; fileType: string }[];
      }
    ) {
      state.attachments = action.payload;
    },
    updateAttachment(
      state: AttachmentsInitialState,
      action: {
        payload: { fileName: string; fileURL: string; fileType: string };
      }
    ) {
      state.attachments.push(action.payload);
    },
    removeAttachment(
      state: AttachmentsInitialState,
      action: { payload: number }
    ) {
      const filterAttachments = state.attachments.filter(
        (_item: any, itemIndex: number) => itemIndex !== action?.payload
      );
      state.deletedAttachmentPaths = [
        ...state.deletedAttachmentPaths,
        state.attachments[action.payload].fileURL
      ];
      state.attachments = filterAttachments;
    },
    updateNavigationBlocking(state: any, action: any) {
      state.blockNavigation = action.payload;
    },
    updateAdjustment(state: any, action: any) {
      const data = action.payload;
      data.total.value = 0;

      Object.values(data).forEach((item: any) => {
        if (item.id === "total" || item.id === "valuation") return;
        data.total.value += stringToNumber(item.value || "") || 0;
      });

      data.total.value = data.total.value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });

      state.adjustment = data;
    },
    editAdjustment(state: any, action: any) {
      const { name, id, value } = action.payload;
      const input = value === "$0-" ? "-" : value;
      const creditItems =
        name === "pending" ? state?.adjustment : state.deniedCredits;
      const newAdjItems = getAdjustedItems(id, input, creditItems);
      if (newAdjItems) {
        if (name === "pending") {
          state.adjustment = newAdjItems;
        } else {
          state.deniedCredits = newAdjItems;
        }
      }
    },
    updateAdjustmentDenied(state: any, action: any) {
      const data = action.payload;
      data.total.value = 0;

      Object.values(data).forEach((item: any) => {
        if (item.id === "total" || item.id === "valuation") return;
        data.total.value += stringToNumber(item.value || "");
      });

      data.total.value = data.total.value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });

      state.deniedCredits = data;
    },
    updateDeniedAdjustment(state: any, action: any) {
      const data = action.payload;
      data.total.value = 0;

      Object.values(data).forEach((item: any) => {
        if (item.id === "total" || item.id === "valuation") return;
        data.total.value += stringToNumber(item.value || "") || 0;
      });

      data.total.value = data.total.value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });

      state.deniedCredits = data;
    },
    updateNotes(state: any, action: any) {
      const notes = action.payload;
      state.notes = notes;
    },
    reset() {
      return initialState;
    },
    updateCreatedBy(state: any, action: any) {
      state.createdBy = action.payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(createInventoryAdjustment.pending, (state) => {
        state.loading = true;
      })
      .addCase(createInventoryAdjustment.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createInventoryAdjustment.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchCreditTable.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCreditTable.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.status = "idle";
        state.pendingCreditsData = transformDataToDepartment(payload.data);
      })
      .addCase(fetchCreditTable.rejected, (state, _action) => {
        state.loading = false;
        state.status = "error";
        state.data = {};
      });
  }
});

export const {
  updateAttachment,
  setAttachment,
  updateNotes,
  updateNavigationBlocking,
  removeAttachment,
  updateAdjustment,
  editAdjustment,
  updateAdjustmentDenied,
  updateDeniedAdjustment,
  reset,
  updateCreatedBy
} = createInventoryAdjustmentSlice.actions;

export const selectAdjustment = (state: any) =>
  state.createInventoryAdjustment.adjustment;

export const selectDeniedCredits = (state: any) =>
  state.createInventoryAdjustment.deniedCredits;

export const selectBlockNavigation = (state: any) =>
  state.createInventoryAdjustment.blockNavigation;

export const selectNotes = (state: any) =>
  state.createInventoryAdjustment.notes;

export const selectLoading = (state: any) =>
  state.createInventoryAdjustment.loading;

export const selectCreatedBy = (state: any) =>
  state.createInventoryAdjustment.createdBy;

export const selectAttachments = (state: any) =>
  state.createInventoryAdjustment?.attachments;

export const selectDeletedAttachmentPaths = (state: any) =>
  state.createInventoryAdjustment?.deletedAttachmentPaths;

export const selectPendingCreditData = (state: any) =>
  state.createInventoryAdjustment.pendingCreditsData;

export default createInventoryAdjustmentSlice.reducer;
